import React from "react";
import { ArrowRight } from "lucide-react";
import { useContactModal } from "../context/ContactModalContext";

const PARAGRAPH_CLASS =
  "mt-3 text-sm leading-relaxed text-gray-700 md:text-base";
const CTA_CARD_BG = "#121313";
const BTN_CLASS_DARK =
  "mt-4 inline-flex w-fit items-center gap-2 rounded-2xl border-2 border-white bg-transparent px-6 py-3 text-sm font-semibold tracking-wide text-white transition-colors hover:bg-white hover:text-[#121313]";

function getLeftLabel(section, fallbackIndex) {
  if (section.leftLabel) return section.leftLabel;
  const words = (section.heading || "").split(/\s+/).slice(0, 3).join(" ");
  return words ? words.toUpperCase() : `ABSCHNITT ${fallbackIndex + 1}`;
}

/**
 * hh
 * Renders insight detail body from insight.detail (sections, cta link, optional bottomImage).
 * Uses the same two-column layout (left label + right content) as service, Geschichte, Karriere pages.
 */
export default function InsightDetailContent({ insight }) {
  const { openContactModal } = useContactModal() || {};
  if (!insight?.detail) return null;

  const { detail } = insight;

  const sectionId = (name) => `insight-section-${name}`;

  return (
    <div className="mt-0 w-full">
      {detail.sections?.map((section, i) => (
        <section
          key={i}
          id={sectionId(String(i))}
          className="border-b border-gray-100 py-12 md:py-16 first:pt-10"
        >
          <div className="container-app mx-auto flex flex-col gap-8 px-4 md:flex-row md:gap-12 lg:gap-16">
            <div className="flex-shrink-0 md:w-36 lg:w-40">
              <span className="detail-side-label text-xs font-semibold uppercase tracking-[0.2em] text-gray-700">
                {getLeftLabel(section, i)}
              </span>
            </div>
            <div className="min-w-0 flex-1 max-w-4xl">
              <h2 className="text-2xl font-semibold leading-snug text-gray-900 md:text-3xl">
                {section.heading}
              </h2>
              {section.paragraphs?.map((p, j) => (
                <p key={j} className={PARAGRAPH_CLASS}>
                  {p}
                </p>
              ))}
            </div>
          </div>
        </section>
      ))}

      {detail.cta && (
        <section id={sectionId("cta")} className="border-b border-gray-100 py-10 md:py-12">
          <div className="container-app mx-auto flex flex-col gap-6 px-4 md:flex-row md:gap-10 lg:gap-12">
            <div className="flex-shrink-0 md:w-36 lg:w-40">
              <span className="detail-side-label text-xs font-semibold uppercase tracking-[0.2em] text-gray-700">
                KONTAKT
              </span>
            </div>
            <div className="min-w-0 flex-1 max-w-4xl">
              <div
                className="rounded-2xl p-6 shadow-[0_2px_12px_rgba(0,0,0,0.08)] md:p-8"
                style={{ backgroundColor: CTA_CARD_BG }}
              >
                {detail.cta.heading && (
                  <h2 className="text-xl font-semibold text-white md:text-2xl">
                    {detail.cta.heading}
                  </h2>
                )}
                <button
                  type="button"
                  className={"group " + (detail.cta.heading ? BTN_CLASS_DARK : "mt-6 " + BTN_CLASS_DARK)}
                  onClick={() => openContactModal?.()}
                >
                  {detail.cta.buttonText}
                  <ArrowRight className="h-5 w-6 shrink-0 transition-transform duration-200 ease-out group-hover:translate-x-0.5" aria-hidden />
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {detail.bottomImage && (
        <section id={sectionId("bottom-image")} className="border-b border-gray-100 py-12 md:py-16">
          <div className="container-app mx-auto flex flex-col gap-8 px-4 md:flex-row md:gap-12 lg:gap-16">
            <div className="flex-shrink-0 md:w-36 lg:w-40">
              <span className="detail-side-label text-xs font-semibold uppercase tracking-[0.2em] text-gray-700">
                BILD
              </span>
            </div>
            <div className="min-w-0 flex-1 max-w-4xl">
              {detail.bottomImage.heading && (
                <h2 className="text-2xl font-semibold leading-snug text-gray-900 md:text-3xl">
                  {detail.bottomImage.heading}
                </h2>
              )}
              <div className={`${detail.bottomImage.heading ? "mt-4 " : ""}overflow-hidden rounded-2xl`}>
                <img
                  src={
                    typeof detail.bottomImage.image === "string"
                      ? detail.bottomImage.image
                      : detail.bottomImage.image?.default ?? detail.bottomImage.image
                  }
                  alt={detail.bottomImage.heading || "Beitragsbild"}
                  className="h-[200px] w-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
